import { getStylesValues } from '@wix/tpa-settings';
import type { CreateControllerFn } from '@wix/yoshi-flow-editor';
import stylesParams from '../../../components/Archive/stylesParams';
import { createControllerId, createLogger } from '../../common/controller/helpers';
import { initLazyActions } from '../../common/controller/lazy-actions';
import { getPlatformApi } from '../../common/controller/platform-api';
import { getCurrentSiteLanguage } from '../../common/services/get-current-site-language';
import getEnvironment from '../../common/services/get-environment';
import { isRtlLanguage } from '../../common/services/is-rtl-language';
import listenToInstanceChange from '../../common/services/listen-to-instance-change';
import { toMonitoredViewer } from '../../common/services/monitoring';
import { simulateControllerError } from '../../common/services/simulate-error';
import { subscribeToChange } from '../../common/services/state-optimizer';
import { getInitialStateVersions } from '../../common/services/state-optimizer/change-detector';
import { createReduxStore } from './create-redux-store';
import {
  initializeActions,
  initializePromisifiedActions,
  initializeStoreBaseData,
  refreshDataOnLogin,
} from './init-actions';

const isProduction = process.env.NODE_ENV === 'production';

export const createArchiveController: CreateControllerFn = ({
  controllerConfig: { appParams, compId, config, setProps, platformAPIs },
  flowAPI,
}) =>
  toMonitoredViewer(flowAPI.panoramaClient, 'archive-widget.create-controller', async () => {
    const platformApi = await getPlatformApi(
      platformAPIs.platformApiProvider,
      false,
      flowAPI.panoramaClient,
    );
    const { isSSR, isDebug } = getEnvironment(platformApi);
    const language = getCurrentSiteLanguage(platformApi, flowAPI);
    const log = createLogger(isDebug, isProduction);

    log('createArchiveController', {
      appParams,
      platformApi,
      isSSR,
      language,
    });

    const bundleName = 'archive-widget';

    const store = createReduxStore({
      appParams,
      platformApi,
      compId,
      bundleName,
      flowAPI,
    });

    const actions = initializeActions({
      platformApi,
      store,
    });

    const {
      showNewestPostsFirstV2: showNewestPostsFirst,
      monthsDisplayLimitV2: monthsDisplayLimit,
    } = getStylesValues(config.style.styleParams, stylesParams, {
      isMobile: flowAPI.environment.isMobile,
    });

    const params = {
      showNewestPostsFirst,
      monthsDisplayLimit,
    };

    const pageReady = () =>
      toMonitoredViewer(flowAPI.panoramaClient, 'archive-widget.page-ready', async () => {
        log('createArchiveController.pageReady -> start');

        simulateControllerError(platformApi, 'archive.pageReady'); // window, location

        initLazyActions({ isSSR });

        const actionsPromisified = initializePromisifiedActions({
          store,
        });

        await initializeStoreBaseData({
          platformApi,
          store,
          language,
          platformAPIs,
          bundleName,
          appParams,
          showNewestPostsFirst: Boolean(showNewestPostsFirst),
          monthsDisplayLimit: Number(monthsDisplayLimit),
        });
        listenToInstanceChange(platformApi, appParams, store);

        log('createArchiveController.pageReady -> done');
        const state = store.getState();

        const stateVersions = getInitialStateVersions(state);
        const controllerId = createControllerId();

        setProps({
          state,
          stateVersions,
          actions,
          actionsPromisified,
          isSSR,
          isRTL: isRtlLanguage(language),
          controllerId,
          fitToContentHeight: true,
          publicData: config.publicData,
          stylesParams: config.style.styleParams,
        });

        refreshDataOnLogin({ platformApi, store, router: undefined });
        subscribeToChange(store, stateVersions, setProps, controllerId);
      });

    return {
      pageReady,
      exports: () => {},
      updateConfig(_, configuration) {
        if (flowAPI.environment.isEditor) {
          const { showNewestPostsFirstV2, monthsDisplayLimitV2 } = getStylesValues(
            configuration.style.styleParams,
            stylesParams,
            {
              isMobile: flowAPI.environment.isMobile,
            },
          );

          const booleanStyleParamsToTriggerApiHaveChanged =
            showNewestPostsFirstV2 !== params.showNewestPostsFirst ||
            monthsDisplayLimitV2 !== params.monthsDisplayLimit;

          if (booleanStyleParamsToTriggerApiHaveChanged) {
            params.showNewestPostsFirst = showNewestPostsFirstV2;
            params.monthsDisplayLimit = monthsDisplayLimitV2;

            actions.fetchArchiveWidgetInitialData({
              showNewestPostsFirst: Boolean(showNewestPostsFirstV2),
              monthsDisplayLimit: Number(monthsDisplayLimitV2),
            });
          }
        }
      },
    };
  });
